import { useEffect, useRef, useState } from 'react';

export interface WaveformPin {
  id: number;
  position_ms: number;
  author: { username: string; display_name: string | null; avatar: string | null } | null;
  body: string | null;
}

interface Props {
  url: string | null;
  progress: number;
  onSeek?: (fraction: number) => void;
  height?: number;
  className?: string;
  /** Comments anchored to a moment (§6.3). Rendered as marks under the wave. */
  pins?: WaveformPin[];
  /** Total length, for placing pins. Without it a pin has no fraction. */
  durationMs?: number | null;
  onPinClick?: (pin: WaveformPin) => void;
}

/**
 * The waveform, drawn to a canvas.
 *
 * Canvas rather than 1800 DOM nodes: the peaks file is a fixed 1800 points
 * (§4.3), and that many elements is a layout cost paid on every resize and a
 * paint cost paid on every progress tick. A canvas redraw is one operation.
 *
 * Played and unplayed are different colours because that is information rather
 * than decoration - which is why they have their own tokens rather than
 * borrowing the accent.
 */
export function Waveform({
  url,
  progress,
  onSeek,
  height = 56,
  className = '',
  pins = [],
  durationMs = null,
  onPinClick,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [peaks, setPeaks] = useState<number[] | null>(null);

  useEffect(() => {
    if (!url) {
      setPeaks(null);

      return;
    }

    let cancelled = false;

    fetch(url)
      .then((response) => response.json())
      .then((data: { peaks?: number[] }) => {
        if (!cancelled) setPeaks(data.peaks ?? null);
      })
      .catch(() => {
        // A missing waveform is cosmetic. The track still plays, and a broken
        // image where the waveform should be is worse than a flat bar.
        if (!cancelled) setPeaks(null);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const context = canvas.getContext('2d');

    if (!context) return;

    /*
     * Drawn at device pixel ratio.
     *
     * A canvas sized in CSS pixels on a retina screen is upscaled by the
     * compositor and looks soft - which on a waveform of thin bars is very
     * visible. Backing store at DPR, then scaled back down in CSS.
     */
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    context.scale(dpr, dpr);
    context.clearRect(0, 0, width, height);

    const styles = getComputedStyle(document.documentElement);
    /*
     * Literal fallbacks, deliberately.
     *
     * Canvas takes a colour string, not a CSS value - `fillStyle` has no idea
     * what `var(--color-signal)` means and silently keeps the previous style.
     * These are the last resort for a context where the custom property did not
     * resolve, so they have to be something canvas can actually parse.
     */
    const played = styles.getPropertyValue('--color-signal').trim() || '#7b84f2';
    const unplayed = styles.getPropertyValue('--color-line-mid').trim() || '#2A2A4A';

    const barWidth = 2;
    const gap = 1;
    const bars = Math.floor(width / (barWidth + gap));
    const source = peaks ?? [];

    /*
     * Peak-normalised for display.
     *
     * The stored peaks are true amplitudes relative to full scale, which is the
     * right thing to store - it keeps the information that one track is quieter
     * than another. But rendering them raw means a quiet or dynamic recording
     * draws as a flat line while a squashed master fills the box, and the
     * waveform stops conveying shape at all.
     *
     * So the tallest bar always reaches the top. This is what every player
     * does, and it is a rendering choice rather than a change to the data.
     */
    const loudest = source.length > 0 ? Math.max(...source) : 0;
    const scale = loudest > 0 ? 1 / loudest : 1;

    for (let i = 0; i < bars; i++) {
      // Sampled from the peaks rather than one bar per peak: the bar count
      // depends on how wide the element happens to be, and the peaks file is a
      // fixed width by design.
      const raw = source.length > 0 ? source[Math.floor((i / bars) * source.length)] ?? 0 : 0.08;
      const peak = Math.min(1, raw * scale);

      // A floor, so silence is still a visible line rather than a gap that
      // looks like the waveform failed to load.
      const barHeight = Math.max(2, peak * height);
      const y = (height - barHeight) / 2;

      context.fillStyle = i / bars < progress ? played : unplayed;
      context.fillRect(i * (barWidth + gap), y, barWidth, barHeight);
    }
  }, [peaks, progress, height]);

  /*
   * Pins are DOM, not canvas.
   *
   * The wave is a canvas because 1800 bars would be 1800 nodes redrawn on every
   * progress tick. A pin is the opposite case: there are a handful, they need a
   * tooltip, a link and a click target, and every one of those is free in the
   * DOM and hand-written in a canvas.
   */
  const placed =
    durationMs && durationMs > 0
      ? pins.map((pin) => ({ pin, left: Math.min(1, pin.position_ms / durationMs) }))
      : [];

  const canvas = (
    <canvas
      ref={canvasRef}
      style={{ height, width: '100%' }}
      className={`${onSeek ? 'cursor-pointer' : ''} ${placed.length > 0 ? 'w-full' : className}`}
      // Not a button, but it is interactive, so it needs a role and a label -
      // otherwise it is an invisible control to anybody not using a mouse.
      role={onSeek ? 'slider' : undefined}
      aria-label={onSeek ? 'Seek' : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onClick={(event) => {
        if (!onSeek) return;

        const rect = event.currentTarget.getBoundingClientRect();
        onSeek((event.clientX - rect.left) / rect.width);
      }}
      onKeyDown={(event) => {
        if (!onSeek) return;

        // Five-second steps from the keyboard, which is what every media
        // control does and what somebody will try first.
        if (event.key === 'ArrowRight') onSeek(Math.min(1, progress + 0.02));
        if (event.key === 'ArrowLeft') onSeek(Math.max(0, progress - 0.02));
      }}
    />
  );

  if (placed.length === 0) {
    return canvas;
  }

  return (
    <div className={`relative ${className}`} data-waveform-pins>
      {canvas}

      <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-3">
        {placed.map(({ pin, left }) => (
          <button
            key={pin.id}
            type="button"
            data-pin={pin.id}
            style={{ left: `${left * 100}%` }}
            // The label is the whole comment, because a dot with no text is a
            // control a screen reader cannot describe.
            aria-label={`${pin.author?.display_name || pin.author?.username || 'Someone'} at ${format(pin.position_ms)}: ${pin.body ?? ''}`}
            title={`${pin.author?.display_name || pin.author?.username || 'Someone'}: ${pin.body ?? ''}`}
            onClick={(event) => {
              event.stopPropagation();
              onPinClick?.(pin);
            }}
            className="pointer-events-auto absolute top-0 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-app bg-accent transition-transform hover:scale-125"
          />
        ))}
      </div>
    </div>
  );
}

function format(ms: number): string {
  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
